import React, { useMemo, useState, useRef } from 'react';
import './CopyQuestJsonModal.css';
import { LOCATION_MD_FIELDS, defaultFieldSelection, buildLocationMarkdown, buildQuestMarkdown } from '../questMarkdown';

// Field-picker for Markdown export. Two modes:
//   - 'quest'   : export every location (+ optional quest header) — opened from the menu.
//   - 'location': export a single location — opened from the location card's 📋 menu.
// The field checkboxes are shared; quest mode adds a couple of header toggles.
const ExportMarkdownModal = ({ mode = 'quest', questInfo, locations, location, index, fileBaseName = 'quest', onClose }) => {
  const [fields, setFields] = useState(defaultFieldSelection);
  const [questName, setQuestName] = useState(true);
  const [questDesc, setQuestDesc] = useState(true);
  const [toast, setToast] = useState('');
  const toastTimer = useRef(null);

  const markdown = useMemo(() => {
    if (mode === 'location') return buildLocationMarkdown(location || {}, fields, Number.isInteger(index) ? index : undefined);
    return buildQuestMarkdown(questInfo, locations, fields, { name: questName, description: questDesc });
  }, [mode, location, index, questInfo, locations, fields, questName, questDesc]);

  const toggle = (key) => setFields((prev) => ({ ...prev, [key]: !prev[key] }));
  const setAll = (val) => setFields(LOCATION_MD_FIELDS.reduce((acc, f) => { acc[f.key] = val; return acc; }, {}));
  // "Defaults" restores the header toggles too, so it is a full reset of the picker.
  const resetDefaults = () => { setFields(defaultFieldSelection()); setQuestName(true); setQuestDesc(true); };

  const flashToast = (msg) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(''), 1200);
  };

  const handleCopy = async () => {
    try {
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(markdown);
      } else {
        const ta = document.createElement('textarea');
        ta.value = markdown;
        ta.style.position = 'fixed';
        ta.style.left = '-999999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      flashToast('Copied!');
    } catch (err) {
      console.error('Markdown copy failed:', err);
      flashToast('Copy failed');
    }
  };

  const handleSaveFile = () => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const stamp = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    const safe = String(fileBaseName).replace(/[^\w.-]+/g, '_').slice(0, 80) || 'quest';
    const fileName = `${safe}_${stamp}.md`;
    const blob = new Blob([markdown], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const heading = mode === 'location'
    ? `Export location to Markdown${location && location.name ? ` — ${location.name}` : ''}`
    : 'Export quest to Markdown';

  return (
    <div className="copy-modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="copy-modal-dialog">
        <h2>{heading}</h2>
        <div className="copy-modal-content">
          <div className="field-section">
            <h3>Fields</h3>
            <div className="select-all-buttons">
              <button onClick={() => setAll(true)}>Select All</button>
              <button onClick={() => setAll(false)}>Deselect All</button>
              <button onClick={resetDefaults}>Defaults</button>
            </div>
            {mode === 'quest' && (
              <div className="checkbox-group" style={{ marginBottom: 12, paddingBottom: 12, borderBottom: '1px solid #eee' }}>
                <label>
                  <input type="checkbox" checked={questName} onChange={() => setQuestName((v) => !v)} />
                  Quest title (heading)
                </label>
                <label>
                  <input type="checkbox" checked={questDesc} onChange={() => setQuestDesc((v) => !v)} />
                  Quest description
                </label>
              </div>
            )}
            <div className="checkbox-group">
              {LOCATION_MD_FIELDS.map((f) => (
                <label key={f.key}>
                  <input type="checkbox" checked={!!fields[f.key]} onChange={() => toggle(f.key)} />
                  {f.label}
                </label>
              ))}
            </div>
          </div>
          <div className="field-section">
            <h3>Preview</h3>
            <textarea
              readOnly
              value={markdown}
              style={{ width: '100%', height: 360, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
        </div>
        <div className="copy-modal-actions">
          <button onClick={onClose} className="close-button">Close</button>
          <button onClick={handleSaveFile} className="save-file-button">💾 Save as file</button>
          <div className="copy-button-wrapper">
            <button onClick={handleCopy} className="copy-button">Copy Markdown</button>
            {toast && <span className="copy-toast-inline">{toast}</span>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportMarkdownModal;
